import { useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for tracking activity item visibility using Intersection Observer
 * Automatically marks activities as seen when they're in view for a specified duration
 */
const useActivityVisibility = (onActivitySeen, { threshold = 0.5, viewDuration = 2000 } = {}) => {
  const observerRef = useRef(null)
  const timeoutsRef = useRef(new Map()) // Track timeouts for each activity
  const observedElementsRef = useRef(new Map()) // Track observed elements
  const pausedTimersRef = useRef(new Map()) // Track paused timers when tab is inactive

  // Create intersection observer
  const createObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const activityId = entry.target.dataset.activityId
          if (!activityId) return

          if (entry.isIntersecting) {
            // Clear any existing timeout for this activity
            const existingTimeout = timeoutsRef.current.get(activityId)
            if (existingTimeout) {
              clearTimeout(existingTimeout)
            }

            // Only start timer if tab is currently active
            if (!document.hidden) {
              // Set new timeout to mark as seen after viewDuration
              const timeout = setTimeout(() => {
                onActivitySeen(activityId)
                timeoutsRef.current.delete(activityId)
                pausedTimersRef.current.delete(activityId)
              }, viewDuration)

              timeoutsRef.current.set(activityId, timeout)
            } else {
              // Tab is inactive - store that this item should start timing when tab becomes active
              pausedTimersRef.current.set(activityId, Date.now())
            }
          } else {
            // Element is no longer visible - cancel timer and remove from paused list
            const timeout = timeoutsRef.current.get(activityId)
            if (timeout) {
              clearTimeout(timeout)
              timeoutsRef.current.delete(activityId)
            }
            // Remove from paused timers since it's no longer visible
            pausedTimersRef.current.delete(activityId)
          }
        })
      },
      {
        threshold,
        rootMargin: '0px'
      }
    )
  }, [onActivitySeen, threshold, viewDuration])

  // Initialize observer
  useEffect(() => {
    createObserver()

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      // Clear all pending timeouts
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout))
      timeoutsRef.current.clear()
      pausedTimersRef.current.clear()
    }
  }, [createObserver])

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        timeoutsRef.current.forEach((timeout, activityId) => {
          clearTimeout(timeout)
          // Store when this timer was paused (if not already stored)
          if (!pausedTimersRef.current.has(activityId)) {
            pausedTimersRef.current.set(activityId, Date.now())
          }
        })
        timeoutsRef.current.clear()
      } else {
        // For each paused timer, check if the element is still visible and restart timer
        pausedTimersRef.current.forEach((pausedTime, activityId) => {
          const element = observedElementsRef.current.get(activityId)
          if (element && observerRef.current) {
            // Check if element is currently intersecting
            const rect = element.getBoundingClientRect()
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0 && 
                            rect.left < window.innerWidth && rect.right > 0
            
            if (isVisible) {
              // Restart timer for this activity
              const timeout = setTimeout(() => {
                onActivitySeen(activityId)
                timeoutsRef.current.delete(activityId)
                pausedTimersRef.current.delete(activityId)
              }, viewDuration)
              
              timeoutsRef.current.set(activityId, timeout)
            }
          }
        })
        
        // Clear paused timers that we've handled
        Array.from(pausedTimersRef.current.keys()).forEach(activityId => {
          if (timeoutsRef.current.has(activityId)) {
            pausedTimersRef.current.delete(activityId)
          }
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [onActivitySeen, viewDuration])

  // Observe an activity element
  const observeActivity = useCallback((element, activityId) => {
    if (!element || !activityId || !observerRef.current) return

    // Set data attribute for identification
    element.dataset.activityId = activityId
    
    // Start observing
    observerRef.current.observe(element)
    observedElementsRef.current.set(activityId, element)
  }, [])

  // Stop observing an activity element
  const unobserveActivity = useCallback((activityId) => {
    if (!activityId || !observerRef.current) return

    const element = observedElementsRef.current.get(activityId)
    if (element) {
      observerRef.current.unobserve(element)
      observedElementsRef.current.delete(activityId)
    }

    // Clear any pending timeout
    const timeout = timeoutsRef.current.get(activityId)
    if (timeout) {
      clearTimeout(timeout)
      timeoutsRef.current.delete(activityId)
    }

    // Remove from paused timers
    pausedTimersRef.current.delete(activityId)
  }, [])

  // Manually mark an activity as seen (bypass timer)
  const markActivitySeen = useCallback((activityId) => {
    if (!activityId) return
    
    // Clear any pending timeout
    const timeout = timeoutsRef.current.get(activityId)
    if (timeout) {
      clearTimeout(timeout)
      timeoutsRef.current.delete(activityId)
    }
    
    // Remove from paused timers
    pausedTimersRef.current.delete(activityId)
    
    // Mark as seen immediately
    onActivitySeen(activityId)
  }, [onActivitySeen])

  return {
    observeActivity,
    unobserveActivity,
    markActivitySeen
  }
}

export default useActivityVisibility
